import { Checkbox } from "@tql/ui-components-react/kendo/inputs";
import { useContext } from "react";

import { QuoteDetailsContext } from "~/features/quote/context/quote-details-context.tsx";
import { useQuoteDetailsPageLoadQueries } from "~/features/quote/hooks/use-quote-details-hooks.ts";
import { useIsMobile } from "~/shared/hooks/use-is-mobile.ts";

import styles from "./accessorial-services-list.module.css";
import { AccessorialServicesListMobile } from "./accessorial-services-list-mobile.tsx";

export type AccessorialDetail = {
	id: number;
	name: string;
	code: string | null;
	isHazmat: boolean;
	accessorialUsageTypeId: number;
	accessorialUsageType: { id: number; name: string };
	isActive: boolean;
	stopTypeId: number | null;
	stopType: { id: number; name: string } | null;
	ediCode: string | null;
	p44APICode: string | null;
	isSubjectToFuelSurcharge: boolean;
};

export function AccessorialServicesList({
	disabled = false,
}: { readonly disabled?: boolean } = {}) {
	const { quoteState, onChange } = useContext(QuoteDetailsContext);
	const { data: pageData } = useQuoteDetailsPageLoadQueries();
	const isMobile = useIsMobile();

	const details = (
		pageData?.accessorials?.selectable?.[
			"addDetails" as keyof typeof pageData.accessorials.selectable
		] as AccessorialDetail[] | undefined
	)?.filter(
		(detail) =>
			detail.name.includes("Blind") ||
			detail.name.includes("In Bond") ||
			detail.name.includes("Protect From Freezing"),
	);

	const selectedDetails = quoteState.addtDetails as
		| AccessorialDetail[]
		| undefined;

	const handleSelectionChange = (
		detail: AccessorialDetail,
		checked: boolean,
	) => {
		if (disabled) return;
		const current = quoteState.addtDetails || [];
		const newValue = checked
			? [...current, detail]
			: current.filter(
					(selected: AccessorialDetail) => selected.id !== detail.id,
				);
		onChange("addtDetails", newValue);
	};

	if (isMobile) {
		return (
			<AccessorialServicesListMobile
				details={details ?? []}
				disabled={disabled}
				onChange={(newValue) => {
					if (!disabled) onChange("addtDetails", newValue);
				}}
				selectedDetails={selectedDetails ?? []}
			/>
		);
	}

	return (
		<div className={styles.accessorialServicesItems}>
			{details?.map((detail) => (
				<div key={detail.id} className={"tql-checkbox-item"}>
					<Checkbox
						checked={
							selectedDetails?.some((selected) => selected.id === detail.id) ??
							false
						}
						disabled={disabled}
						id={`addt-detail-${detail.id}`}
						onChange={(event) => handleSelectionChange(detail, event.value)}
						tabIndex={-1}
					/>
					<label
						htmlFor={`addt-detail-${detail.id}`}
						style={{ cursor: "pointer", userSelect: "none" }}
					>
						{detail.name}
					</label>
				</div>
			))}
		</div>
	);
}
